'use strict';

const libWorkspace = require('pict-section-workspace');
const libDocumentLookup = require('../documents/Document-Lookup.js');
const libUserSearch = require('../access/User-Search.js');
const libEntityPickerSearch = require('../services/Entity-Picker-Search.js');

/**
 * InspectionWorkspaceProvider — the engine behind the Inspection workspace (#/Inspection/Workspace/:ID[/:Tab], bridged
 * over /PSRS/Inspection/View/:GUID). One tabbed workspace for a scheduled/dynamic asset inspection, on plain Meadow CRUD
 * (server InspectionsBundle — no bespoke endpoints, no server work):
 *   • Overview   — StatusDisplay (denormalized string; the InspectionStatus table has no live read route) + type (resolved
 *                  from IDInspectionType — TypeDisplay comes back empty) + the four dates + project link; inline edit of
 *                  the schedulable fields (Name/Description/ScheduledStartDate/DeadlineDate/Type).
 *   • Assets     — InspectionPhysicalAssetJoin {IDInspection,IDPhysicalAsset}; add via a searchable asset picker / remove.
 *   • Documents  — InspectionDocumentJoin {…,IncludeOnDossier,Ordinal}: the dossier flag + ordering, Open/View (same-origin
 *                  HTML via HeadlightDocumentRender — NEVER Fieldbook) + PDF; add / remove / reorder / toggle-dossier.
 *   • Inspectors — InspectionUserJoin {IDInspection,IDUser}; add / remove. (Phase-3 Team assignment plugs in here.)
 *   • Timeline   — InspectionEventLog {Event,IDUser,Datum} — read-only audit trail.
 * Config in [[Inspection.config.js]]. Modeled on [[TeamWorkspace-Provider.js]].
 */
class InspectionWorkspaceProvider extends libWorkspace.HeadlightWorkspaceBaseProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);
		/** @type {any} */
		this.pict;
		this._inspection = null;
		this._tabs = {};            // per-tab {Loading, Loaded, Error, Rows, ...}
		this._names = { user: {}, project: {}, asset: {}, type: {}, team: {} };  // id -> display name/meta caches
		this._typeOptions = [];     // [{IDInspectionType, Name}] — small (per-customer, ~11), safe to load whole
		this._editing = false;      // Overview "Edit details" open?
		this._docMeta = {};         // IDDocument -> {Name, Locked, DocumentType}
		this._packetDocIDs = [];    // the inspection's document-packet IDDocuments (for team grants across the packet)
	}

	get idInspection() { return this._inspection ? this._inspection.IDInspection : null; }
	get idCustomer() { return (this._inspection && this._inspection.IDCustomer) || (this.pict.AppData && this.pict.AppData.CurrentUser && this.pict.AppData.CurrentUser.CustomerID) || 0; }
	get providerName() { return (this.options && this.options.ProviderIdentifier) || 'InspectionWorkspace'; }
	get _docRender() { return this.pict.HeadlightDocumentRender; }

	// --- REST helpers (mirror the Team workspace) ---
	_get(pURL) { return this._getJSON(pURL); }
	_postJSON(pURL, pPayload) { return new Promise((resolve, reject) => this.restClient.postJSON(pURL, pPayload, (e, b) => e ? reject(e) : resolve(b))); }
	_putJSON(pURL, pPayload) { return new Promise((resolve, reject) => this.restClient.putJSON(pURL, pPayload, (e, b) => e ? reject(e) : resolve(b))); }
	_deleteJSON(pURL)
	{
		return new Promise((resolve, reject) =>
		{
			const m = /^([A-Za-z][A-Za-z0-9]*)\/(\d+)$/.exec(String(pURL));
			if (m) { this.restClient.deleteEntity(m[1], m[2], (e, b) => e ? reject(e) : resolve(b)); return; }
			const tmpBase = this.restClient.serverURL || '/1.0/';
			fetch(`${tmpBase}${pURL}`, { method: 'DELETE', credentials: 'include' })
				.then((r) => { if (!r.ok) { reject(new Error(`HTTP ${r.status}`)); return; } r.text().then((t) => resolve(t ? JSON.parse(t) : {})).catch(() => resolve({})); })
				.catch(reject);
		});
	}
	_fmtDate(pV) { if (!pV) { return ''; } try { const d = new Date(pV); if (!Number.isNaN(d.getTime())) { return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }); } } catch (e) { /* */ } return String(pV).slice(0, 10); }
	_dateInput(pV) { if (!pV) { return ''; } return String(pV).slice(0, 10); }

	// --- hydrate: base loaded the scalar Inspection; refresh it, resolve project + type vocab ---
	async hydrateAsync(pRecord)
	{
		this._tabs = {}; this._editing = false;
		const tmpFull = await this._get(`Inspection/${pRecord.IDInspection}`);
		if (tmpFull && tmpFull.IDInspection) { Object.assign(pRecord, tmpFull); }
		this._inspection = pRecord;
		// Inspection types are a small per-customer vocabulary — load them all once (TypeDisplay is not denormalized).
		try
		{
			const types = await this._get('InspectionTypes/FilteredTo/FBV~Deleted~EQ~0~FSF~Name~ASC~0/0/200');
			this._typeOptions = (Array.isArray(types) ? types : []).map((t) => ({ IDInspectionType: t.IDInspectionType, Name: t.Name || `Type ${t.IDInspectionType}` }));
			this._typeOptions.forEach((t) => { this._names.type[t.IDInspectionType] = t.Name; });
		}
		catch (e) { this._typeOptions = []; }
		pRecord.TypeDisplay = this._names.type[pRecord.IDInspectionType] || pRecord.TypeDisplay || '';
		if (pRecord.IDProject)
		{
			try { const pr = await this._get(`Projects/Lite/FilteredTo/FBV~IDProject~EQ~${Number(pRecord.IDProject)}/0/1`); const row = Array.isArray(pr) ? pr[0] : null; if (row) { this._names.project[pRecord.IDProject] = row.Value || row.Name || `Project ${pRecord.IDProject}`; } }
			catch (e) { /* */ }
		}
	}

	// --- per-tab load + repaint ---
	async _ensureTabDataAsync(pTab, pForce)
	{
		if (!this._inspection) { return; }
		if (!pForce && this._tabs[pTab] && this._tabs[pTab].Loaded) { this._scheduleMount(pTab); return; }
		this._tabs[pTab] = { Loading: true, Loaded: false };
		this.repaintBody();
		try
		{
			if (pTab === 'overview') { await this._loadOverview(); }
			else if (pTab === 'assets') { this._tabs[pTab].Rows = await this._loadAssets(); }
			else if (pTab === 'documents') { this._tabs[pTab].Rows = await this._loadDocuments(); }
			else if (pTab === 'inspectors') { this._tabs[pTab].Rows = await this._loadInspectors(); }
			else if (pTab === 'teams') { this._tabs[pTab].Rows = await this._loadTeams(); }
			else if (pTab === 'timeline') { this._tabs[pTab].Rows = await this._loadTimeline(); }
			this._tabs[pTab].Loaded = true;
		}
		catch (e) { this._tabs[pTab].Error = (e && e.message) || String(e); this._tabs[pTab].Loaded = true; }
		finally { this._tabs[pTab].Loading = false; this.repaintBody(); this._scheduleMount(pTab); }
	}
	_reload(pTab) { if (this._tabs[pTab]) { this._tabs[pTab].Loaded = false; } this._ensureTabDataAsync(pTab, true); }
	_state(pTab) { return this._tabs[pTab] || {}; }
	_loadingOrError(pTab)
	{
		const t = this._state(pTab);
		if (t.Loading || (!t.Loaded && !t.Error)) { return `<div class="iw-note"><i class="fas fa-circle-notch fa-spin"></i> Loading…</div>`; }
		if (t.Error) { return `<div class="iw-error">${this._esc(t.Error)}</div>`; }
		return null;
	}

	async _resolveNames(pKind, pIDs, pURL, pLabel)
	{
		const tmpMissing = [ ...new Set(pIDs.map(Number).filter((x) => x && !this._names[pKind][x])) ];
		if (!tmpMissing.length) { return; }
		const rows = await this._get(pURL(tmpMissing));
		(Array.isArray(rows) ? rows : []).forEach((r) => { const id = pLabel.id(r); if (id) { this._names[pKind][id] = pLabel.name(r); } });
	}

	// --- data loaders ---
	async _loadOverview()
	{
		const [ assets, docs, users ] = await Promise.all([
			this._get(`InspectionPhysicalAssetJoins/Count/FilteredTo/FBV~IDInspection~EQ~${this.idInspection}~FBV~Deleted~EQ~0`),
			this._get(`InspectionDocumentJoins/Count/FilteredTo/FBV~IDInspection~EQ~${this.idInspection}~FBV~Deleted~EQ~0`),
			this._get(`InspectionUserJoins/Count/FilteredTo/FBV~IDInspection~EQ~${this.idInspection}~FBV~Deleted~EQ~0`),
		]);
		this._tabs.overview = Object.assign(this._tabs.overview || {}, {
			Assets: (assets && assets.Count) || 0,
			Documents: (docs && docs.Count) || 0,
			Inspectors: (users && users.Count) || 0,
		});
	}
	async _loadAssets()
	{
		const rows = await this._get(`InspectionPhysicalAssetJoins/FilteredTo/FBV~IDInspection~EQ~${this.idInspection}~FBV~Deleted~EQ~0/0/200`);
		const list = Array.isArray(rows) ? rows : [];
		await this._resolveNames('asset', list.map((r) => r.IDPhysicalAsset),
			(ids) => `PhysicalAssets/FilteredTo/FBL~IDPhysicalAsset~INN~${ids.join(',')}/0/${ids.length}`,
			{ id: (a) => a.IDPhysicalAsset, name: (a) => JSON.stringify({ Name: a.Name || `Asset ${a.IDPhysicalAsset}`, AssetNumber: a.AssetNumber || '', Structure: a.ExternalSyncGUID || '', GUID: a.GUIDPhysicalAsset || '' }) });
		return list;
	}
	async _loadDocuments()
	{
		const rows = await this._get(`InspectionDocumentJoins/FilteredTo/FBV~IDInspection~EQ~${this.idInspection}~FBV~Deleted~EQ~0~FSF~Ordinal~ASC~0/0/300`);
		const list = Array.isArray(rows) ? rows : [];
		const tmpDocIDs = [ ...new Set(list.map((r) => r.IDDocument).filter(Boolean)) ];
		this._docMeta = this._docMeta || {};
		if (tmpDocIDs.length)
		{
			const docs = await this._get(`Documents/FilteredTo/FBL~IDDocument~INN~${tmpDocIDs.join(',')}/0/${tmpDocIDs.length}`);
			(Array.isArray(docs) ? docs : []).forEach((d) => { this._docMeta[d.IDDocument] = { Name: d.Name || `Document ${d.IDDocument}`, Locked: !!(d.Locked || d.IsLocked), DocumentType: d.DocumentType }; });
		}
		return list;
	}
	async _loadInspectors()
	{
		const rows = await this._get(`InspectionUserJoins/FilteredTo/FBV~IDInspection~EQ~${this.idInspection}~FBV~Deleted~EQ~0/0/200`);
		const list = Array.isArray(rows) ? rows : [];
		await this._resolveNames('user', list.map((r) => r.IDUser), (ids) => `Users/FilteredTo/FBL~IDUser~INN~${ids.join(',')}/0/${ids.length}`, { id: (u) => u.IDUser, name: (u) => [ u.NameFirst, u.NameLast ].filter(Boolean).join(' ') || u.LoginID || `#${u.IDUser}` });
		return list;
	}
	// Teams are assigned by granting them the inspection's whole document packet (DocumentAccessJoin per doc) — the same
	// "derived via document grants" mechanism as Team→Project; the access engine projects it to members and their project
	// reach. No InspectionTeamJoin. This loader derives the assigned teams by grouping packet-doc grants by IDTeam.
	async _loadPacket()
	{
		const idj = await this._get(`InspectionDocumentJoins/FilteredTo/FBV~IDInspection~EQ~${this.idInspection}~FBV~Deleted~EQ~0/0/300`);
		const docIDs = [ ...new Set((Array.isArray(idj) ? idj : []).map((r) => r.IDDocument).filter(Boolean)) ];
		this._packetDocIDs = docIDs;
		this._docMeta = this._docMeta || {};
		const missing = docIDs.filter((id) => !this._docMeta[id]);
		if (missing.length)
		{
			const docs = await this._get(`Documents/FilteredTo/FBL~IDDocument~INN~${missing.join(',')}/0/${missing.length}`);
			(Array.isArray(docs) ? docs : []).forEach((d) => { this._docMeta[d.IDDocument] = { Name: d.Name || `Document ${d.IDDocument}`, Locked: !!(d.Locked || d.IsLocked), DocumentType: d.DocumentType }; });
		}
		return docIDs;
	}
	async _loadTeams()
	{
		const docIDs = await this._loadPacket();
		if (!docIDs.length) { return []; }
		const rows = await this._get(`DocumentAccessJoins/FilteredTo/FBL~IDDocument~INN~${docIDs.join(',')}~FBV~Deleted~EQ~0/0/500`);
		const byTeam = {};
		(Array.isArray(rows) ? rows : []).forEach((g) => { const t = Number(g.IDTeam) || 0; if (!t) { return; } if (!byTeam[t]) { byTeam[t] = new Set(); } byTeam[t].add(g.IDDocument); });
		const teamIDs = Object.keys(byTeam).map(Number);
		await this._resolveNames('team', teamIDs, (ids) => `Teams/FilteredTo/FBL~IDTeam~INN~${ids.join(',')}/0/${ids.length}`, { id: (t) => t.IDTeam, name: (t) => t.Name || `Team ${t.IDTeam}` });
		return teamIDs.map((t) => ({ IDTeam: t, Covered: byTeam[t].size, Total: docIDs.length })).sort((a, b) => b.Covered - a.Covered);
	}
	async _loadTimeline()
	{
		const rows = await this._get(`InspectionEventLogs/FilteredTo/FBV~IDInspection~EQ~${this.idInspection}~FBV~Deleted~EQ~0~FSF~IDInspectionEventLog~DESC~0/0/200`);
		const list = Array.isArray(rows) ? rows : [];
		await this._resolveNames('user', list.map((r) => r.IDUser), (ids) => `Users/FilteredTo/FBL~IDUser~INN~${ids.join(',')}/0/${ids.length}`, { id: (u) => u.IDUser, name: (u) => [ u.NameFirst, u.NameLast ].filter(Boolean).join(' ') || u.LoginID || `#${u.IDUser}` });
		return list;
	}

	_assetMeta(pID) { const raw = this._names.asset[pID]; if (!raw) { return { Name: `Asset ${pID}` }; } try { return JSON.parse(raw); } catch (e) { return { Name: raw }; } }
	_statusClass(pStatus)
	{
		const s = String(pStatus || '').toLowerCase();
		if (s.indexOf('complete') >= 0 || s.indexOf('closed') >= 0) { return 'is-complete'; }
		if (s.indexOf('progress') >= 0 || s.indexOf('active') >= 0 || s.indexOf('field') >= 0) { return 'is-progress'; }
		if (s.indexOf('schedul') >= 0 || s.indexOf('pending') >= 0 || s === '') { return 'is-scheduled'; }
		return 'is-other';
	}

	// =========================================================== interactions ===========================================================
	toggleEdit() { this._editing = !this._editing; this.repaintBody(); this._scheduleMount('overview'); }
	async saveDetails()
	{
		const CA = this.pict.ContentAssignment; const g = (sel) => { const el = CA.getElement(sel); return (el && el.length) ? el[0].value : undefined; };
		const payload = { IDInspection: this.idInspection };
		const name = g('#InspectionWorkspace-Name'); if (name !== undefined) { payload.Name = name; }
		const desc = g('#InspectionWorkspace-Description'); if (desc !== undefined) { payload.Description = desc; }
		const sched = g('#InspectionWorkspace-Scheduled'); if (sched !== undefined) { payload.ScheduledStartDate = sched || null; }
		const dead = g('#InspectionWorkspace-Deadline'); if (dead !== undefined) { payload.DeadlineDate = dead || null; }
		const type = g('#InspectionWorkspace-Type'); if (type !== undefined) { payload.IDInspectionType = Number(type) || 0; }
		try
		{
			await this._putJSON('Inspection', payload);
			// reflect the edits on the in-memory record (incl. resolved type display for the header/overview)
			Object.assign(this._inspection, payload);
			this._inspection.TypeDisplay = this._names.type[this._inspection.IDInspectionType] || this._inspection.TypeDisplay || '';
		}
		catch (e) { this.pict.log.error(`Save inspection failed: ${e.message || e}`); }
		this._editing = false;
		this.repaintBody(); this._scheduleMount('overview');
	}
	async addAsset(pIDAsset)
	{
		const id = Number(pIDAsset) || 0; if (!id) { return; }
		try { await this._postJSON('InspectionPhysicalAssetJoin', { IDInspection: this.idInspection, IDPhysicalAsset: id, IDCustomer: this.idCustomer }); }
		catch (e) { this.pict.log.error(`Add asset failed: ${e.message || e}`); }
		this._reload('assets');
	}
	async removeAsset(pIDJoin) { const id = Number(pIDJoin) || 0; if (!id) { return; } try { await this._deleteJSON(`InspectionPhysicalAssetJoin/${id}`); } catch (e) { this.pict.log.error(`Remove asset failed: ${e.message || e}`); } this._reload('assets'); }
	async addDocument(pIDDocument)
	{
		const id = Number(pIDDocument) || 0; if (!id) { return; }
		const rows = this._state('documents').Rows || [];
		const nextOrdinal = rows.reduce((m, r) => Math.max(m, Number(r.Ordinal) || 0), -1) + 1;
		try { await this._postJSON('InspectionDocumentJoin', { IDInspection: this.idInspection, IDDocument: id, IncludeOnDossier: true, Ordinal: nextOrdinal, IDCustomer: this.idCustomer }); }
		catch (e) { this.pict.log.error(`Add document failed: ${e.message || e}`); }
		this._reload('documents');
	}
	async removeDocument(pIDJoin) { const id = Number(pIDJoin) || 0; if (!id) { return; } try { await this._deleteJSON(`InspectionDocumentJoin/${id}`); } catch (e) { this.pict.log.error(`Remove document failed: ${e.message || e}`); } this._reload('documents'); }
	async toggleDossier(pIDJoin)
	{
		const id = Number(pIDJoin) || 0; if (!id) { return; }
		const row = (this._state('documents').Rows || []).find((r) => r.IDInspectionDocumentJoin === id); if (!row) { return; }
		try { await this._putJSON('InspectionDocumentJoin', { IDInspectionDocumentJoin: id, IncludeOnDossier: !(row.IncludeOnDossier) }); }
		catch (e) { this.pict.log.error(`Toggle dossier failed: ${e.message || e}`); }
		this._reload('documents');
	}
	async moveDocument(pIDJoin, pDir)
	{
		const rows = (this._state('documents').Rows || []).slice(); const i = rows.findIndex((r) => r.IDInspectionDocumentJoin === Number(pIDJoin));
		const j = i + (pDir < 0 ? -1 : 1); if (i < 0 || j < 0 || j >= rows.length) { return; }
		const a = rows[i]; const b = rows[j]; const ao = Number(a.Ordinal) || 0; const bo = Number(b.Ordinal) || 0;
		try { await Promise.all([ this._putJSON('InspectionDocumentJoin', { IDInspectionDocumentJoin: a.IDInspectionDocumentJoin, Ordinal: bo }), this._putJSON('InspectionDocumentJoin', { IDInspectionDocumentJoin: b.IDInspectionDocumentJoin, Ordinal: ao }) ]); }
		catch (e) { this.pict.log.error(`Reorder failed: ${e.message || e}`); }
		this._reload('documents');
	}
	async addInspector(pIDUser)
	{
		if (!this.canStaff) { this.pict.log.warn('Only a team leader can change who is on an inspection.'); return; }
		const id = Number(pIDUser) || 0; if (!id) { return; }
		try { await this._postJSON('InspectionUserJoin', { IDInspection: this.idInspection, IDUser: id, IDCustomer: this.idCustomer }); }
		catch (e) { this.pict.log.error(`Add inspector failed: ${e.message || e}`); }
		this._reload('inspectors');
	}
	async removeInspector(pIDJoin) { if (!this.canStaff) { this.pict.log.warn('Only a team leader can change who is on an inspection.'); return; } const id = Number(pIDJoin) || 0; if (!id) { return; } try { await this._deleteJSON(`InspectionUserJoin/${id}`); } catch (e) { this.pict.log.error(`Remove inspector failed: ${e.message || e}`); } this._reload('inspectors'); }
	// Assign a team = grant it EVERY document in the inspection's packet (Steven's decision: grant the whole packet, since a
	// grant on the dossier doc does not propagate to its poly-children). Idempotent: skips docs the team already holds.
	async assignTeam(pIDTeam)
	{
		const idTeam = Number(pIDTeam) || 0; if (!idTeam) { return; }
		const docIDs = (this._packetDocIDs && this._packetDocIDs.length) ? this._packetDocIDs : await this._loadPacket();
		if (!docIDs.length) { this.pict.log.warn('This inspection has no documents to grant — attach its packet first.'); return; }
		const existing = new Set();
		try { const rows = await this._get(`DocumentAccessJoins/FilteredTo/FBV~IDTeam~EQ~${idTeam}~FBL~IDDocument~INN~${docIDs.join(',')}~FBV~Deleted~EQ~0/0/500`); (Array.isArray(rows) ? rows : []).forEach((g) => existing.add(g.IDDocument)); }
		catch (e) { /* treat as none owned */ }
		const toGrant = docIDs.filter((id) => !existing.has(id));
		try
		{
			await Promise.all(toGrant.map((id) => { const meta = (this._docMeta || {})[id] || {}; return this._postJSON('DocumentAccessJoin', { IDTeam: idTeam, IDDocument: id, DocumentType: meta.DocumentType || '', IDRole: 0, AccessKind: 'Owner', GrantRule: 'Config', IDCustomer: this.idCustomer }); }));
		}
		catch (e) { this.pict.log.error(`Assign team failed: ${e.message || e}`); }
		this._reload('teams');
	}
	async unassignTeam(pIDTeam)
	{
		const idTeam = Number(pIDTeam) || 0; if (!idTeam) { return; }
		const docIDs = (this._packetDocIDs && this._packetDocIDs.length) ? this._packetDocIDs : await this._loadPacket();
		if (!docIDs.length) { this._reload('teams'); return; }
		try
		{
			const rows = await this._get(`DocumentAccessJoins/FilteredTo/FBV~IDTeam~EQ~${idTeam}~FBL~IDDocument~INN~${docIDs.join(',')}~FBV~Deleted~EQ~0/0/500`);
			const joins = (Array.isArray(rows) ? rows : []).map((g) => g.IDDocumentAccessJoin).filter(Boolean);
			await Promise.all(joins.map((j) => this._deleteJSON(`DocumentAccessJoin/${j}`)));
		}
		catch (e) { this.pict.log.error(`Unassign team failed: ${e.message || e}`); }
		this._reload('teams');
	}
	// Same-origin HTML render (viewer/editor) — NEVER Fieldbook; falls back to PDF if the service is missing.
	openHTML(pIDDocument, pReadOnly)
	{
		const id = Number(pIDDocument) || 0; if (!id) { return; }
		const svc = this._docRender;
		if (!svc || (typeof svc.openDocument !== 'function')) { this.downloadPDF(id); return; }
		svc.openDocument(id, { target: '_blank', variation: pReadOnly ? 'html' : 'default' }, (pError) => { if (pError) { try { this.pict.log.error(`Open document ${id} failed: ${pError.message || pError}`); } catch (e) { /* */ } } });
	}
	downloadPDF(pIDDocument) { const id = Number(pIDDocument) || 0; if (!id) { return; } const svc = this._docRender; if (svc && (typeof svc.downloadPDFLink === 'function')) { svc.downloadPDFLink(id); return; } try { window.open(`${this.restClient.serverURL || '/1.0/'}Document/${id}/DCR.pdf`, '_blank', 'noopener'); } catch (e) { /* */ } }
	openAsset(pIDAsset) { const id = Number(pIDAsset) || 0; if (id) { try { window.location.hash = `#/Asset/Workspace/${id}`; } catch (e) { /* */ } } }
	openProject() { const id = Number(this._inspection && this._inspection.IDProject) || 0; if (id) { try { window.location.hash = `#/Project/Workspace/${id}`; } catch (e) { /* */ } } }

	// =========================================================== tab bodies ===========================================================
	_overviewHTML()
	{
		const t = this._inspection || {}; const s = this._state('overview'); const P = this.providerName;
		const loading = this._loadingOrError('overview');
		const status = t.StatusDisplay || 'Scheduled';
		const typeName = this._names.type[t.IDInspectionType] || t.TypeDisplay || '';
		const now = new Date();
		const overdue = t.DeadlineDate && !(/complete|closed/i.test(status)) && (new Date(t.DeadlineDate).getTime() < now.getTime());
		if (this._editing)
		{
			const opts = this._typeOptions.map((o) => `<option value="${o.IDInspectionType}"${o.IDInspectionType === t.IDInspectionType ? ' selected' : ''}>${this._esc(o.Name)}</option>`).join('');
			return `<div class="iw-editgrid">
				<label>Name<input id="InspectionWorkspace-Name" type="text" value="${this._esc(t.Name || '')}"></label>
				<label>Type<select id="InspectionWorkspace-Type">${opts}</select></label>
				<label>Scheduled start<input id="InspectionWorkspace-Scheduled" type="date" value="${this._dateInput(t.ScheduledStartDate)}"></label>
				<label>Deadline<input id="InspectionWorkspace-Deadline" type="date" value="${this._dateInput(t.DeadlineDate)}"></label>
				<label>Description<textarea id="InspectionWorkspace-Description">${this._esc(t.Description || '')}</textarea></label>
			</div>
			<div class="iw-editbar"><button class="iw-btn iw-ghost" onclick="_Pict.providers.${P}.toggleEdit()">Cancel</button><button class="iw-btn" onclick="_Pict.providers.${P}.saveDetails()"><i class="fas fa-check"></i> Save details</button></div>`;
		}
		const stats = loading ? '' : `<div class="iw-stats">
			<div class="iw-stat"><div class="iw-stat-v">${(s.Assets || 0).toLocaleString()}</div><div class="iw-stat-l">Assets</div></div>
			<div class="iw-stat"><div class="iw-stat-v">${(s.Documents || 0).toLocaleString()}</div><div class="iw-stat-l">Documents</div></div>
			<div class="iw-stat"><div class="iw-stat-v">${(s.Inspectors || 0).toLocaleString()}</div><div class="iw-stat-l">Inspectors</div></div>
		</div>`;
		return `<div class="iw-pillrow">
				<span class="iw-status ${this._statusClass(status)}">${this._esc(status)}</span>
				${typeName ? `<span class="iw-typepill">${this._esc(typeName)}</span>` : ''}
				<span style="flex:1"></span>
				<button class="iw-btn iw-ghost" onclick="_Pict.providers.${P}.toggleEdit()"><i class="fas fa-pen"></i> Edit details</button>
			</div>
			${stats}
			<div class="iw-fields">
				<div><div class="iw-field-label">Scheduled start</div><div class="iw-field-value">${t.ScheduledStartDate ? this._esc(this._fmtDate(t.ScheduledStartDate)) : '<span class="iw-muted">— not set —</span>'}</div></div>
				<div><div class="iw-field-label">Deadline</div><div class="iw-field-value ${overdue ? 'iw-overdue' : ''}">${t.DeadlineDate ? this._esc(this._fmtDate(t.DeadlineDate)) + (overdue ? ' · overdue' : '') : '<span class="iw-muted">— not set —</span>'}</div></div>
				<div><div class="iw-field-label">Started</div><div class="iw-field-value">${t.StartDate ? this._esc(this._fmtDate(t.StartDate)) : '<span class="iw-muted">—</span>'}</div></div>
				<div><div class="iw-field-label">Ended</div><div class="iw-field-value">${t.EndDate ? this._esc(this._fmtDate(t.EndDate)) : '<span class="iw-muted">—</span>'}</div></div>
				<div><div class="iw-field-label">Project</div><div class="iw-field-value">${t.IDProject ? `<a class="iw-link" href="#/Project/Workspace/${t.IDProject}">${this._esc(this._names.project[t.IDProject] || ('Project ' + t.IDProject))} <i class="fas fa-arrow-up-right-from-square" style="font-size:0.7em"></i></a>` : '<span class="iw-muted">—</span>'}</div></div>
			</div>
			${t.Description ? `<div class="iw-sec-h">Description</div><div class="iw-field-value">${this._esc(t.Description)}</div>` : ''}
			<div class="iw-hint"><i class="fas fa-circle-info"></i> Status is set by the inspection's forms and the access engine; edit the schedulable fields (name, type, dates) here.</div>`;
	}

	_assetsHTML()
	{
		const loading = this._loadingOrError('assets'); if (loading) { return loading; }
		const P = this.providerName; const rows = this._state('assets').Rows || [];
		const add = `<div class="iw-addbar"><div class="iw-f"><span class="iw-f-l">Add an asset under inspection</span><div id="InspectionWorkspace-AssetPicker" class="iw-pick"></div></div></div>`;
		const body = rows.length ? `<table class="iw-table"><thead><tr><th>Asset</th><th>Asset #</th><th>Structure</th><th></th></tr></thead><tbody>${rows.map((r) => { const m = this._assetMeta(r.IDPhysicalAsset); return `<tr>
			<td><a class="iw-link" href="#/Asset/Workspace/${r.IDPhysicalAsset}">${this._esc(m.Name || ('Asset ' + r.IDPhysicalAsset))}</a></td>
			<td>${m.AssetNumber ? `<span class="iw-chip">${this._esc(m.AssetNumber)}</span>` : '<span class="iw-muted">—</span>'}</td>
			<td>${m.Structure ? `<span class="iw-chip">${this._esc(m.Structure)}</span>` : '<span class="iw-muted">—</span>'}</td>
			<td style="text-align:right"><button class="iw-x" title="Remove from inspection" onclick="_Pict.providers.${P}.removeAsset(${r.IDInspectionPhysicalAssetJoin})"><i class="fas fa-xmark"></i></button></td>
		</tr>`; }).join('')}</tbody></table>` : `<div class="iw-empty">No assets on this inspection yet — add the structure(s) being inspected above.</div>`;
		return add + body;
	}

	_documentsHTML()
	{
		const loading = this._loadingOrError('documents'); if (loading) { return loading; }
		const P = this.providerName; const rows = this._state('documents').Rows || [];
		const add = `<div class="iw-addbar"><div class="iw-f"><span class="iw-f-l">Attach a document to this inspection</span><div id="InspectionWorkspace-DocPicker" class="iw-pick"></div></div></div>`;
		const hint = `<div class="iw-hint"><i class="fas fa-folder-open"></i> The document packet, in dossier order. Toggle the star to include a document in the compiled dossier; open a form as read-only HTML or (when unlocked) editable — never Fieldbook.</div>`;
		const body = rows.length ? `<table class="iw-table"><thead><tr><th style="width:2.5rem">Dossier</th><th>Document</th><th>Type</th><th style="width:4.5rem">Order</th><th></th></tr></thead><tbody>${rows.map((r, i) => { const m = (this._docMeta || {})[r.IDDocument] || {}; const on = !!r.IncludeOnDossier; const locked = !!m.Locked; return `<tr>
			<td style="text-align:center"><i class="fas fa-star iw-dossier ${on ? 'on' : 'off'}" title="${on ? 'On the dossier — click to exclude' : 'Not on the dossier — click to include'}" onclick="_Pict.providers.${P}.toggleDossier(${r.IDInspectionDocumentJoin})"></i></td>
			<td><span class="iw-name">${this._esc(m.Name || ('Document ' + r.IDDocument))}</span>${locked ? ' <i class="fas fa-lock iw-muted" title="Locked — read-only" style="font-size:0.75em"></i>' : ''}</td>
			<td><span class="iw-chip">${this._esc(r.DocumentType || m.DocumentType || '—')}</span></td>
			<td style="white-space:nowrap"><button class="iw-ord" title="Move up"${i === 0 ? ' disabled' : ''} onclick="_Pict.providers.${P}.moveDocument(${r.IDInspectionDocumentJoin}, -1)"><i class="fas fa-arrow-up"></i></button><button class="iw-ord" title="Move down"${i === rows.length - 1 ? ' disabled' : ''} onclick="_Pict.providers.${P}.moveDocument(${r.IDInspectionDocumentJoin}, 1)"><i class="fas fa-arrow-down"></i></button></td>
			<td style="text-align:right; white-space:nowrap">
				${locked ? '' : `<button class="iw-act" title="Open editable HTML" onclick="_Pict.providers.${P}.openHTML(${r.IDDocument}, false)"><i class="fas fa-pen-to-square"></i> Edit</button> `}
				<button class="iw-act" title="View read-only HTML" onclick="_Pict.providers.${P}.openHTML(${r.IDDocument}, true)"><i class="fas fa-eye"></i> View</button>
				<button class="iw-act iw-act-icon" title="Download PDF" onclick="_Pict.providers.${P}.downloadPDF(${r.IDDocument})"><i class="fas fa-file-pdf"></i></button>
				<button class="iw-x" title="Remove from inspection" onclick="_Pict.providers.${P}.removeDocument(${r.IDInspectionDocumentJoin})"><i class="fas fa-xmark"></i></button>
			</td>
		</tr>`; }).join('')}</tbody></table>` : `<div class="iw-empty">No documents attached yet. A scheduled inspection's forms are generated from its type — attach them here to build the dossier.</div>`;
		return add + hint + body;
	}

	/**
	 * May this session staff an inspection — add or unassign inspectors?
	 *
	 * Whoever leads a team can. Leadership is a role held on the TEAM (TeamUserJoin.IDRole), not on the
	 * inspection: an inspection records only who worked it. So a lead staffs crews wherever they are
	 * pulled in, which is how the work actually runs — leads hold different specialisms and bring each
	 * other in as ordinary inspectors depending on the structure.
	 *
	 * Program-tier users (administrators) can always staff. This is a UI affordance, not enforcement —
	 * the server does not gate InspectionUserJoin writes.
	 *
	 * @return {Boolean}
	 */
	get canStaff()
	{
		const tmpScope = this.pict.providers.InspectionScope;
		if (!tmpScope || !tmpScope.scope) { return true; }
		if (tmpScope.scope.Tier === 'program') { return true; }
		return (tmpScope.scope.LedTeamIDs || []).length > 0;
	}

	_inspectorsHTML()
	{
		const loading = this._loadingOrError('inspectors'); if (loading) { return loading; }
		const P = this.providerName; const rows = this._state('inspectors').Rows || [];
		const canStaff = this.canStaff;
		const add = canStaff
			? `<div class="iw-addbar"><div class="iw-f"><span class="iw-f-l">Assign an inspector</span><div id="InspectionWorkspace-UserPicker" class="iw-pick"></div></div></div>`
			: '';
		const hint = canStaff
			? `<div class="iw-hint"><i class="fas fa-circle-info"></i> Individual assignments. Team-based assignment (auto-granting access through the engine) arrives in the Teams phase.</div>`
			: `<div class="iw-hint"><i class="fas fa-circle-info"></i> Only a team leader can change who is on an inspection.</div>`;
		const body = rows.length ? `<table class="iw-table"><thead><tr><th>Inspector</th>${canStaff ? '<th></th>' : ''}</tr></thead><tbody>${rows.map((r) => `<tr>
			<td><span class="iw-name">${this._esc(this._names.user[r.IDUser] || ('#' + r.IDUser))}</span></td>
			${canStaff ? `<td style="text-align:right"><button class="iw-x" title="Unassign" onclick="_Pict.providers.${P}.removeInspector(${r.IDInspectionUserJoin})"><i class="fas fa-user-minus"></i></button></td>` : ''}
		</tr>`).join('')}</tbody></table>` : `<div class="iw-empty">No inspectors assigned yet.</div>`;
		return add + hint + body;
	}

	_teamsHTML()
	{
		const loading = this._loadingOrError('teams'); if (loading) { return loading; }
		const P = this.providerName; const rows = this._state('teams').Rows || [];
		const add = `<div class="iw-addbar"><div class="iw-f"><span class="iw-f-l">Assign a team to this inspection</span><div id="InspectionWorkspace-TeamPicker" class="iw-pick"></div></div></div>`;
		const hint = `<div class="iw-hint"><i class="fas fa-users"></i> Assigning a team grants it every document in this inspection's packet. Its members can open and edit them, and reach the inspection's project, without being added to the project.</div>`;
		const body = rows.length ? `<table class="iw-table"><thead><tr><th>Team</th><th>Packet coverage</th><th></th></tr></thead><tbody>${rows.map((r) => `<tr>
			<td><a class="iw-link" href="#/Team/Workspace/${r.IDTeam}">${this._esc(this._names.team[r.IDTeam] || ('Team ' + r.IDTeam))}</a></td>
			<td><span class="iw-chip">${r.Covered} of ${r.Total} docs</span>${r.Covered < r.Total ? ' <span class="iw-muted" title="Some packet documents are not shared with this team">partial</span>' : ''}</td>
			<td style="text-align:right"><button class="iw-x" title="Unassign team (revoke the whole packet)" onclick="_Pict.providers.${P}.unassignTeam(${r.IDTeam})"><i class="fas fa-users-slash"></i></button></td>
		</tr>`).join('')}</tbody></table>` : `<div class="iw-empty">No teams assigned yet — assign one above to grant its members the whole packet.</div>`;
		return add + hint + body;
	}

	_timelineHTML()
	{
		const loading = this._loadingOrError('timeline'); if (loading) { return loading; }
		const rows = this._state('timeline').Rows || [];
		const body = rows.length ? `<table class="iw-table"><thead><tr><th>When</th><th>Event</th><th>By</th><th>Detail</th></tr></thead><tbody>${rows.map((r) => `<tr>
			<td style="white-space:nowrap">${this._esc(this._fmtDate(r.CreateDate))}</td>
			<td><span class="iw-name">${this._esc(r.Event || '—')}</span></td>
			<td>${r.IDUser ? this._esc(this._names.user[r.IDUser] || ('#' + r.IDUser)) : '<span class="iw-muted">system</span>'}</td>
			<td>${r.Datum ? this._esc(String(r.Datum).slice(0, 200)) : '<span class="iw-muted">—</span>'}</td>
		</tr>`).join('')}</tbody></table>` : `<div class="iw-empty">No events logged for this inspection yet.</div>`;
		return `<div class="iw-hint"><i class="fas fa-clock-rotate-left"></i> Read-only audit trail (most recent first).</div>${body}`;
	}

	// =========================================================== pickers ===========================================================
	_scheduleMount(pTab) { try { setTimeout(() => this._mountPickers(pTab), 0); } catch (e) { /* */ } }
	_picker() { return this.pict.providers && this.pict.providers['Pict-Section-Picker']; }
	_mountPickers(pTab)
	{
		const tmpPicker = this._picker(); const tmpCA = this.pict.ContentAssignment;
		if (!tmpPicker || (typeof tmpPicker.createPicker !== 'function') || !tmpCA) { return; }
		const mount = (pHostID, pName, pDataProvider, pOnChange, pPlaceholder, pExtra) =>
		{
			if (tmpCA.getElement(`#${pHostID}`).length === 0) { return; }
			const v = tmpPicker.createPicker(`${this.providerName}-${pName}`, Object.assign({
				DestinationAddress: `#${pHostID}`, ValueAddress: `AppData.${this.providerName}.${pName}`,
				Searchable: true, AllowClear: true, ClearLabel: `— ${pPlaceholder} —`, Placeholder: `${pPlaceholder}…`,
				DataProvider: pDataProvider, ResolveValue: () => Promise.resolve(null), OnChange: pOnChange,
			}, pExtra || {}));
			if (v && (typeof v.render === 'function')) { v.render(); }
		};
		if (pTab === 'assets') { mount('InspectionWorkspace-AssetPicker', 'AssetPicker', this._assetDataProvider(), (val) => { if (val) { this.addAsset(val); } }, 'search assets'); }
		else if (pTab === 'documents')
		{
			mount('InspectionWorkspace-DocPicker', 'DocPicker', this._docDataProvider(), (val) => { if (val) { this.addDocument(val); } }, 'name or number in this project',
				{ EmptyLabel: (pTerm) => libDocumentLookup.documentSearchEmptyLabel(pTerm, this._inspectionProject) });
		}
		else if (pTab === 'inspectors') { if (this.canStaff) { mount('InspectionWorkspace-UserPicker', 'UserPicker', this._userDataProvider(), (val) => { if (val) { this.addInspector(val); } }, 'search people'); } }
		else if (pTab === 'teams') { mount('InspectionWorkspace-TeamPicker', 'TeamPicker', this._teamDataProvider(), (val) => { if (val) { this.assignTeam(val); } }, 'search teams'); }
	}
	_teamDataProvider()
	{
		return libEntityPickerSearch.entityPickerDataProvider((pURL) => this._get(pURL),
			{
				Set: 'Teams', IDField: 'IDTeam', Scope: () => `FBV~IDCustomer~EQ~${this.idCustomer}~FBV~Deleted~EQ~0`,
				ToOption: (r) => ({ Value: Number(r.IDTeam), Text: r.Name || `Team ${r.IDTeam}`, Record: r }),
			});
	}
	_assetDataProvider()
	{
		return libEntityPickerSearch.entityPickerDataProvider((pURL) => this._get(pURL),
			{
				Set: 'PhysicalAssets', IDField: 'IDPhysicalAsset', Scope: () => `FBV~IDCustomer~EQ~${this.idCustomer}~FBV~Deleted~EQ~0`,
				ToOption: (r) => ({ Value: Number(r.IDPhysicalAsset), Text: (r.Name || `Asset ${r.IDPhysicalAsset}`) + (r.ExternalSyncGUID ? ` · ${r.ExternalSyncGUID}` : ''), Record: r }),
			});
	}
	get _inspectionProject()
	{
		return Number(this._inspection && this._inspection.IDProject) || 0;
	}
	// Documents are found by number, or by name within the inspection's project (never a customer-wide name scan).
	_docDataProvider()
	{
		return libDocumentLookup.documentPickerDataProvider((pURL) => this._get(pURL), () => ({ IDCustomer: this.idCustomer, IDProject: this._inspectionProject }));
	}
	_userDataProvider()
	{
		return libUserSearch.userPickerDataProvider((pURL) => this._get(pURL), { Scope: 'FBV~Deleted~EQ~0', PageSize: 25 });
	}
}

module.exports = InspectionWorkspaceProvider;
module.exports.default_configuration = { ProviderIdentifier: 'InspectionWorkspace' };
